/**
 * #363 — the bar of the map window when at least one group the user can see has a site drawing: Google map | Site
 * map, and, while the site map shows and there are two or more drawings, which group's drawing. While pins are being
 * arranged the switch and the select hold still (`locked`): leaving would drop the unsaved moves without a word.
 */
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { SitePlanSummary } from '@/types'

export function MapModeSwitch({ plans, site, groupId, locked = false, inline = false, onChange }: {
  plans: SitePlanSummary[]
  /** The site map is shown (else the Google map). */
  site: boolean
  /** The drawing shown, or the one the site map opens on. */
  groupId: number | null
  /** Pins are being arranged on the site map: nothing here changes until they are saved or cancelled. */
  locked?: boolean
  /** #368 — rendered in the map card's title row (no bar border or padding) instead of as the bar above the map. */
  inline?: boolean
  onChange: (next: { site: boolean; groupId: number | null }) => void
}) {
  const { t } = useTranslation('cameras')
  const hintId = useId()
  const hint = t('map.site.lockedHint')
  // No overflow clip on the pair, so the focus ring shows (review M2); the ends carry the rounding.
  const segment = (on: boolean) => cn(
    'relative h-7 px-3 text-xs font-medium transition-colors focus-visible:z-10',
    on ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground',
    locked && 'cursor-not-allowed opacity-50 hover:text-muted-foreground',
  )
  const lockedProps = locked ? { 'aria-disabled': true as const, title: hint, 'aria-describedby': hintId } : {}
  const choose = (next: { site: boolean; groupId: number | null }) => { if (!locked) onChange(next) }
  return (
    <div data-map-mode-bar className={cn('flex shrink-0 flex-wrap items-center gap-2', !inline && 'border-b border-border px-3 py-1.5')}>
      <div className="inline-flex rounded-md border border-border" role="group" aria-label={t('map.site.switchLabel')}>
        <button type="button" aria-pressed={!site} className={cn(segment(!site), 'rounded-l-md')} onClick={() => choose({ site: false, groupId })} {...lockedProps}>
          {t('map.site.google')}
        </button>
        <button type="button" aria-pressed={site} className={cn(segment(site), 'rounded-r-md border-l border-border')} onClick={() => choose({ site: true, groupId })} {...lockedProps}>
          {t('map.site.site')}
        </button>
      </div>
      {site && plans.length > 1 && (
        <select
          aria-label={t('map.site.group')}
          value={groupId ?? ''}
          disabled={locked}
          title={locked ? hint : undefined}
          aria-describedby={locked ? hintId : undefined}
          onChange={(e) => choose({ site: true, groupId: Number(e.target.value) })}
          className="h-7 max-w-[14rem] rounded-md border border-border bg-background px-2 text-xs text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          {plans.map((p) => <option key={p.group_id} value={p.group_id}>{p.group_name}</option>)}
        </select>
      )}
      {locked && <span id={hintId} className="sr-only">{hint}</span>}
    </div>
  )
}
